import React from 'react';

import { Card, Empty, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { AdminCityRow, AdminUserDetail } from 'models/Lead';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import appI18n from '../../../../config/i18n';
import { LeadPaymentCatalogs } from '../useLeadCatalogs';
import PaymentAccountSection from './PaymentAccountSection';
import PaymentShippingSection from './PaymentShippingSection';

const KEY = 'scenes.leads.components.detail_lead_modal.sections.payment';

interface Props {
  detail: AdminUserDetail;
  catalogs: LeadPaymentCatalogs;
  cities: AdminCityRow[];
  citiesLoading: boolean;
  onUpdated: (next: AdminUserDetail) => void;
}

/**
 * "Datos de pago" tab: two cards edited independently. The admin view shows
 * no missing-data messages (those are lead-facing): only required fields
 * while editing, like the other sections.
 */
function PaymentSections({
  detail,
  catalogs,
  cities,
  citiesLoading,
  onUpdated,
}: Props) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const payment = detail.payment;

  if (!payment) {
    return (
      <Card size="small" className="lead-section-card">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical" size={2}>
              <Typography.Text strong>
                {t(`${KEY}.not_applicable_title`)}
              </Typography.Text>
              <Typography.Text type="secondary">
                {t(`${KEY}.not_applicable_description`)}
              </Typography.Text>
            </Space>
          }
        />
      </Card>
    );
  }

  const dateFormat = FunctionsHelper.getDateFormatByLang(i18n.language);

  return (
    <div>
      <PaymentAccountSection
        detail={detail}
        payment={payment}
        catalogs={catalogs}
        onUpdated={onUpdated}
      />
      <PaymentShippingSection
        detail={detail}
        payment={payment}
        cities={cities}
        citiesLoading={citiesLoading}
        onUpdated={onUpdated}
      />
      {payment.updatedAt && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t(`${KEY}.last_update`, {
            date: dayjs(payment.updatedAt).format(`${dateFormat} h:mm A`),
          })}
        </Typography.Text>
      )}
    </div>
  );
}

export default PaymentSections;
